import { useEffect, useRef, useState } from "react";
import { CheckCircle2, RefreshCw } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/features/auth/AuthContext";
import { authApiClient } from "@/features/auth/session";
import { postAuthPath } from "@/features/auth/postAuthPath";
import { useWorkspace } from "@/features/workspaces/WorkspaceContext";
import {
  captureExtensionConnectContext,
  extensionConnectPath,
  getConnectedExtensionState,
  pairExtension,
  type ExtensionConnectContext,
} from "@/lib/extensionConnect";
import { Button } from "@/shared/ui/button";
import { ErrorAlert } from "@/shared/ui/error-alert";
import { Spinner } from "@/shared/ui/spinner";
import { useI18n } from "@/i18n/I18nContext";

export function ConnectExtensionPage() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { workspaces, currentWorkspace, ready, error: workspaceError, reload } = useWorkspace();
  const [context, setContext] = useState<ExtensionConnectContext | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(true);
  const requestKey = useRef<string | null>(null);

  useEffect(() => {
    setContext(captureExtensionConnectContext(params.toString()));
  }, [params]);

  const connect = async (nextContext: ExtensionConnectContext, workspaceId: string) => {
    const key = `${nextContext.clientId}:${nextContext.extensionId}:${workspaceId}`;
    if (requestKey.current === key) return;
    requestKey.current = key;
    setConnecting(true);
    setError(null);
    try {
      const connected = await getConnectedExtensionState();
      if (
        !connected?.signedIn ||
        connected.accountId !== user?.user_id ||
        connected.workspaceId !== workspaceId
      ) {
        await pairExtension(
          nextContext,
          (clientId, extensionId, workspaceId) =>
            authApiClient.createConnectCode(clientId, extensionId, workspaceId),
          workspaceId,
        );
      }
      navigate(postAuthPath(user, workspaces, "/meetings"), { replace: true });
    } catch (nextError) {
      requestKey.current = null;
      setError(
        nextError instanceof Error
          ? nextError.message
          : t("connect.error"),
      );
    } finally {
      setConnecting(false);
    }
  };

  useEffect(() => {
    if (!context || !ready || workspaceError) return;
    if (workspaces.length === 0) {
      navigate(`/onboarding?redirect=${encodeURIComponent(extensionConnectPath(context))}`, { replace: true });
      return;
    }
    if (currentWorkspace) void connect(context, currentWorkspace.id);
    // The connection callback is deliberately triggered only when its context
    // or workspace readiness changes; its state updates must not restart it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, ready, workspaceError, workspaces.length, currentWorkspace?.id]);

  return (
    <main className="flex min-h-dvh items-center justify-center bg-canvas px-6 py-12">
      <section className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center shadow-sm">
        <span className="mx-auto flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <CheckCircle2 className="size-5" aria-hidden="true" />
        </span>
        <h1 className="mt-5 text-xl font-semibold">{t("connect.title")}</h1>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">
          {t("connect.description")}
        </p>
        {!context ? (
          <ErrorAlert className="mt-6 text-left" message={t("connect.missingDetails")} />
        ) : workspaceError ? (
          <div className="mt-6 space-y-4 text-left">
            <ErrorAlert message={workspaceError} />
            <Button className="w-full" onClick={() => void reload()}>
              <RefreshCw className="size-4" aria-hidden="true" />
              {t("common.retry")}
            </Button>
          </div>
        ) : connecting ? (
          <Spinner className="mt-5" label={t("connect.connecting")} />
        ) : error ? (
          <div className="mt-6 space-y-4 text-left">
            <ErrorAlert message={error} />
            <Button className="w-full" onClick={() => { if (currentWorkspace) void connect(context, currentWorkspace.id); }}>
              <RefreshCw className="size-4" aria-hidden="true" />
              {t("common.retry")}
            </Button>
          </div>
        ) : null}
      </section>
    </main>
  );
}
